import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import MainLayout from '../components/MainLayout'
import { useAuth } from '../context/AuthContext'
import {
  getUserProfile,
  updateUserProfile,
  updatePassword,
  updateEmail,
  deleteUserAccount,
  runBackup,
  UserProfile,
  UpdatePasswordData,
  UpdateEmailData
} from '../api/user'
import ConfirmDialog from '../components/ConfirmDialog'

// Storage type display info
const storageTypes: Record<string, { label: string; icon: string }> = {
  gdrive: { label: 'Google Drive™', icon: '/static/images/gdrive.png' },
}

// Theme options (colors defined in index.css via CSS variables)
const themes = [
  { value: 'lightgray', label: 'Light Theme' },
  { value: 'classic', label: 'Classic Theme' },
  { value: 'darkgray', label: 'Dark Theme' },
]

export default function ProfilePage() {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Form states
  const [fullName, setFullName] = useState('')
  const [nameChanged, setNameChanged] = useState(false)

  const [selectedTheme, setSelectedTheme] = useState('lightgray')

  // Password change
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Email change
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [emailPassword, setEmailPassword] = useState('')

  // Messages
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  // Delete dialogs
  const [showDeleteDataDialog, setShowDeleteDataDialog] = useState(false)
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  // Backup state
  const [backupLoading, setBackupLoading] = useState(false)
  const [lastBackupTime, setLastBackupTime] = useState<number | null>(null)
  const [backupFailed, setBackupFailed] = useState(false)

  // Check URL params for storage setup result
  useEffect(() => {
    const storageResult = searchParams.get('storage')
    const error = searchParams.get('error')

    if (storageResult === 'configured') {
      setSuccessMessage('Google Drive storage configured successfully!')
      // Clear the URL params
      setSearchParams({})
    } else if (error === 'storage_setup_failed') {
      setErrorMessage('Failed to setup storage. Please try again.')
      setSearchParams({})
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      const data = await getUserProfile()
      setProfile(data)
      setFullName(data.full_name || '')
      setSelectedTheme(data.theme || 'lightgray')
      setLastBackupTime(data.backed_up_time)
      setBackupFailed(data.backed_up_success === false)

      // Apply theme from profile
      const userTheme = data.theme || 'lightgray'
      document.documentElement.setAttribute('data-theme', userTheme)
      localStorage.setItem('theme', userTheme)
    } catch (err) {
      console.error('Failed to load profile:', err)
      setErrorMessage('Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg)
    setErrorMessage('')
    setTimeout(() => setSuccessMessage(''), 3000)
  }

  const showError = (msg: string) => {
    setErrorMessage(msg)
    setSuccessMessage('')
    setTimeout(() => setErrorMessage(''), 5000)
  }

  const handleUpdateName = async () => {
    if (!nameChanged) return
    setSaving(true)
    try {
      await updateUserProfile({ full_name: fullName })
      setNameChanged(false)
      showSuccess('Name updated successfully')
    } catch (err) {
      showError('Failed to update name')
    } finally {
      setSaving(false)
    }
  }

  const handleThemeChange = async (theme: string) => {
    setSelectedTheme(theme)
    try {
      await updateUserProfile({ theme })

      // Apply theme using data-theme attribute (matches CSS in index.css)
      document.documentElement.setAttribute('data-theme', theme)

      // Store in localStorage for persistence across page loads
      localStorage.setItem('theme', theme)

      showSuccess('Theme updated successfully')
    } catch (err) {
      showError('Failed to update theme')
    }
  }

  const handleSetupStorage = () => {
    // Navigate directly to /setup-storage route which handles OAuth redirect
    // This matches the NiceGUI flow exactly
    window.location.href = '/setup-storage'
  }

  const handleBackup = async () => {
    if (!profile?.storage_type) {
      showError('Please setup storage first')
      return
    }

    setBackupLoading(true)
    try {
      await runBackup()
      showSuccess('Backup completed successfully')
      setBackupFailed(false)
      setLastBackupTime(Math.floor(Date.now() / 1000))
      loadProfile() // Refresh to get updated backup time
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      setBackupFailed(true)
      showError(error.response?.data?.detail || 'Backup failed')
    } finally {
      setBackupLoading(false)
    }
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()

    if (newPassword !== confirmPassword) {
      showError('Passwords do not match')
      return
    }

    if (newPassword.length < 8) {
      showError('Password must be at least 8 characters')
      return
    }

    setSaving(true)
    try {
      const data: UpdatePasswordData = {
        current_password: currentPassword,
        new_password: newPassword
      }
      await updatePassword(data)
      showSuccess('Password updated successfully')
      setShowPasswordForm(false)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      if (error.response?.data?.detail === 'incorrect_password') {
        showError('Current password is incorrect')
      } else {
        showError('Failed to update password')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!newEmail || !emailPassword) {
      showError('Please fill in all fields')
      return
    }

    setSaving(true)
    try {
      const data: UpdateEmailData = {
        current_password: emailPassword,
        new_email: newEmail
      }
      await updateEmail(data)
      showSuccess('Email updated successfully')
      setShowEmailForm(false)
      setNewEmail('')
      setEmailPassword('')
      loadProfile() // Reload to get updated email
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      if (error.response?.data?.detail === 'incorrect_password') {
        showError('Current password is incorrect')
      } else {
        showError('Failed to update email')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (deleteConfirmText.toLowerCase() !== 'delete my account') {
      showError('Please type "delete my account" to confirm')
      return
    }

    try {
      await deleteUserAccount()
      await logout()
      navigate('/')
    } catch (err) {
      showError('Failed to delete account')
    }
  }

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Never'
    return new Date(timestamp * 1000).toLocaleString()
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto">
        {/* Page Header */}
        <div className="flex items-center space-x-3 mb-6">
          <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">
            {successMessage}
          </div>
        )}
        {errorMessage && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
            {errorMessage}
          </div>
        )}

        {/* Subscription Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription</h2>

          {profile?.is_premium ? (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary text-white">
                  Premium
                </span>
                {/*<span className="text-sm text-gray-600">({profile.plan_type})</span>*/}
              </div>
              {profile.premium_end && (
                <p className="text-sm text-gray-600">
                  {profile.premium_auto_renew ? 'Next payment' : 'Subscription ends'}: {formatDate(profile.premium_end)}
                </p>
              )}
              {profile.premium_auto_renew ? (
                <p className="text-sm text-green-600">Auto-renewal is ON</p>
              ) : (
                <p className="text-sm text-yellow-600">Auto-renewal is OFF</p>
              )}
              <a
                href="/manage-subscription"
                className="inline-block px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary transition-colors"
              >
                Manage Subscription
              </a>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Free Plan</span>
              <div className="space-x-2">
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary transition-colors"
                >
                  Upgrade to Premium
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Profile Information Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile Information</h2>

          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value)
                  setNameChanged(e.target.value !== (profile?.full_name || ''))
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              />
              {nameChanged && (
                <>
                  <button
                    onClick={handleUpdateName}
                    disabled={saving}
                    className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      setFullName(profile?.full_name || '')
                      setNameChanged(false)
                    }}
                    className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <div className="flex items-center justify-between">
              <span className="text-gray-900">{profile?.email}</span>
              <div className="space-x-2">
                <button
                  onClick={() => setShowEmailForm(!showEmailForm)}
                  className="px-3 py-1.5 text-sm text-primary hover:text-secondary"
                >
                  Change Email
                </button>
                <button
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                  className="px-3 py-1.5 text-sm text-primary hover:text-secondary"
                >
                  Change Password
                </button>
              </div>
            </div>
          </div>

          {/* Email Change Form */}
          {showEmailForm && (
            <form onSubmit={handleEmailChange} className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium mb-3">Change Email</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">New Email</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Current Password</label>
                  <input
                    type="password"
                    value={emailPassword}
                    onChange={(e) => setEmailPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary disabled:opacity-50"
                  >
                    Update Email
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailForm(false)
                      setNewEmail('')
                      setEmailPassword('')
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Password Change Form */}
          {showPasswordForm && (
            <form onSubmit={handlePasswordChange} className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium mb-3">Change Password</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    required
                    minLength={8}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    required
                    minLength={8}
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary disabled:opacity-50"
                  >
                    Update Password
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordForm(false)
                      setCurrentPassword('')
                      setNewPassword('')
                      setConfirmPassword('')
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Theme Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Theme</h2>
          <div className="flex flex-wrap gap-4">
            {themes.map((theme) => (
              <label key={theme.value} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="theme"
                  value={theme.value}
                  checked={selectedTheme === theme.value}
                  onChange={() => handleThemeChange(theme.value)}
                  className="w-4 h-4 text-primary focus:ring-primary"
                />
                <span className="ml-2 text-gray-700">{theme.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Cloud Storage Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Backup Cloud Storage</h2>

          {profile?.storage_type ? (
            <div className="space-y-3">
              {/* Storage Type with Icon */}
              <div className="flex items-center space-x-2">
                <span className="text-gray-600">Storage Type:</span>
                {storageTypes[profile.storage_type] ? (
                  <>
                    <img
                      src={storageTypes[profile.storage_type].icon}
                      alt={profile.storage_type}
                      className="w-6 h-6"
                    />
                    <span className="font-medium">{storageTypes[profile.storage_type].label}</span>
                  </>
                ) : (
                  <span className="font-medium">{profile.storage_type}</span>
                )}
              </div>

              {profile.storage_email && (
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600">Storage Email:</span>
                  <span className="font-medium">{profile.storage_email}</span>
                </div>
              )}

              {profile.storage_user_name && (
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600">Storage User Name:</span>
                  <span className="font-medium">{profile.storage_user_name}</span>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <span className="text-gray-600">Last Backup Time:</span>
                <span className="font-medium">
                  {lastBackupTime ? formatDate(lastBackupTime) : 'Never'}
                </span>
              </div>

              {backupFailed && (
                <p className="text-red-500 text-sm">
                  Last backup failed. Please reauthorize the storage and try again.
                </p>
              )}

              {/* Backup and Setup Buttons */}
              <div className="flex items-center space-x-3 mt-4">
                <button
                  onClick={handleBackup}
                  disabled={backupLoading}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {backupLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Backing up...</span>
                    </>
                  ) : (
                    <span>Backup Now</span>
                  )}
                </button>

                <button
                  onClick={handleSetupStorage}
                  disabled={backupLoading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Manage Storage
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-gray-600">
                If you want to use Google Drive automated backup, please authorize ReadLocker to access your Google Drive account.
                This will allow ReadLocker to store your notes on Google Drive.
                ReadLocker will have access to files created by ReadLocker only - no other files will be accessible.
              </p>

              <button
                onClick={handleSetupStorage}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary transition-colors flex items-center space-x-2"
              >
                <img src="/static/images/gdrive.png" alt="Google Drive" className="w-5 h-5" />
                <span>Authorize ReadLocker to use Google Drive</span>
              </button>
            </div>
          )}
        </div>

        {/* Danger Zone */}
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-lg font-semibold text-red-600">Danger Zone</h2>
          </div>

          <div className="space-y-4">
            {/* Delete Data */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Delete All My Data</p>
                <p className="text-sm text-gray-600">Delete all notes and tags. Your account will remain active.</p>
              </div>
              <button
                onClick={() => setShowDeleteDataDialog(true)}
                className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap"
              >
                Delete Data
              </button>
            </div>

            {/* Delete Account */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Delete My Account</p>
                <p className="text-sm text-gray-600">Permanently delete your account and all data.</p>
              </div>
              <button
                onClick={() => setShowDeleteAccountDialog(true)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors whitespace-nowrap"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>

        {/* Delete Data Dialog */}
        <ConfirmDialog
          isOpen={showDeleteDataDialog}
          onCancel={() => setShowDeleteDataDialog(false)}
          onConfirm={async () => {
            // This would call a delete-data endpoint
            // For now, just close the dialog
            setShowDeleteDataDialog(false)
            showSuccess('Data deletion feature coming soon')
          }}
          title="Delete All Data"
          message="Are you sure you want to delete all your notes and tags? This action cannot be undone."
          confirmLabel="Delete Data"
          cancelLabel="Cancel"
          confirmVariant="danger"
        />

        {/* Delete Account Dialog */}
        {showDeleteAccountDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-center space-x-2 mb-4">
                <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h3 className="text-lg font-semibold text-red-600">Delete Account</h3>
              </div>

              <p className="text-gray-600 mb-4">
                This action is <strong>permanent</strong> and cannot be undone. All your data will be deleted.
              </p>

              <p className="text-sm text-gray-600 mb-2">
                Type <strong>"delete my account"</strong> to confirm:
              </p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 mb-4"
                placeholder="delete my account"
              />

              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowDeleteAccountDialog(false)
                    setDeleteConfirmText('')
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText.toLowerCase() !== 'delete my account'}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  )
}
